<script setup lang="ts">
import recordingIcon from "@/assets/images/recording-based-icon-white.svg";
const store = useSongStore();
const media = useMediaPlayerStore();
const route = useRoute();
const songNumber = computed(() => route.params.songNumber as string);

const placeholderNotLoaded = computed(() => store.status !== LoadStatus.Ready);
const placeholderNotAuthorized = computed(
  () =>
    store.status === LoadStatus.Ready &&
    !store.song?.products.general.authorized,
);

const songMedia = computed(() => {
  const songs = store.data?.media.filter((m) => m.type === "song");
  if (!songs?.length) {
    return null;
  }
  return songs[0];
});
const albumArtwork = computed(() => {
  return songMedia.value?.artwork || null;
});
async function loadSong() {
  await waitUntilTrue(() => store.song);

  if (store.song?.media.length) {
    await waitUntilTrue(() => store.media.length);
  }
  media.nowPlayingId = "";
  // media.nowPlayingId = store.media[0].id;
  //  media.pause();
}
onMounted(loadSong);
watch(songNumber, loadSong);
</script>
<template>
  <Container class="main-container above-fold-splash">
    <Card
      class="wrapper"
      :background="store.bgSplashImgUrl"
      background-alt="Background Image"
    >
      <div class="splash-wrapper">
        <div class="items-center gap-10 md:flex">
          <div
            v-if="store.status !== LoadStatus.Ready || !!albumArtwork"
            class="album-artwork-wrapper max-md:hidden"
          >
            <span
              v-if="store.status !== LoadStatus.Ready"
              class="dark placeholder aspect-square h-full w-full"
            />
            <AlbumArtwork
              v-else-if="albumArtwork"
              :url="albumArtwork"
              :alt="store.title"
              :restrict-width="250"
            />
          </div>
          <div class="flex-1">
            <div class="mt-5 flex items-center gap-2">
              <div
                v-if="albumArtwork"
                class="album-artwork-wrapper md:hidden"
              >
                <AlbumArtwork
                  :url="albumArtwork"
                  :alt="store.title"
                  :restrict-width="250"
                />
              </div>
              <h1>
                <div v-if="placeholderNotLoaded">
                  <span
                    class="dark placeholder"
                    style="width: 200px"
                  />
                </div>
                <div v-else>{{ store.title }}</div>
              </h1>
              <div>
                <AddToFavorites
                  v-if="!placeholderNotLoaded"
                  class="add-to-fav"
                  on-busy-bg
                  :song-number="songNumber"
                  :title="store.title"
                />
              </div>
              <div
                class="based-on-recording-badge"
                :title="$t('SV.LIT_BasedOnRecording')"
              >
                <!-- <img
                  :src="recordingIcon"
                  alt="Recording Icon"
                  class="based-on-recording-icon"
                /> -->
                <i
                  class="based-on-recording-icon fa-light fa-microphone-stand fa-xl"
                />
                <div class="based-on-recording-text">
                  {{ $t("SV.LIT_BasedOnRecording") }}
                </div>
              </div>
            </div>

            <div class="meta-data">
              <div class="flex flex-wrap items-center gap-5">
                <div class="meta-data-group">
                  <div
                    v-if="!placeholderNotLoaded"
                    class="meta-label"
                  >
                    {{ $t("S.LIT_Album") }}
                  </div>
                  <div
                    v-else
                    class="meta-label"
                  >
                    <span
                      style="width: 80px"
                      class="dark placeholder"
                    />
                  </div>
                  <div
                    v-if="!placeholderNotLoaded"
                    class="meta-value"
                  >
                    <span class="authors">{{ store.song?.album }}</span>
                  </div>
                  <div
                    v-else
                    class="meta-value"
                  >
                    <span
                      class="dark placeholder"
                      style="width: 100px"
                    />
                  </div>
                </div>
                <div class="meta-data-group">
                  <div
                    v-if="!placeholderNotLoaded"
                    class="meta-label"
                  >
                    {{ $t("SV.LIT_Artist") }}
                  </div>
                  <div
                    v-else
                    class="meta-label"
                  >
                    <span
                      style="width: 80px"
                      class="dark placeholder"
                    />
                  </div>
                  <div
                    v-if="!placeholderNotLoaded"
                    class="meta-value"
                  >
                    <span class="authors">{{ store.song?.artist }}</span>
                  </div>
                  <div
                    v-else
                    class="meta-value"
                  >
                    <span
                      v-for="i of 4"
                      :key="i"
                      class="link mr-1 text-lg"
                      ><span
                        class="dark placeholder"
                        style="width: 100px"
                    /></span>
                  </div>
                </div>
              </div>
              <div
                v-if="placeholderNotAuthorized || placeholderNotLoaded"
                class="flex items-start gap-5 md:items-center"
              >
                <div class="meta-data-group">
                  <div class="meta-label">
                    <span
                      class="dark placeholder"
                      style="width: 80px"
                    />
                  </div>
                  <div class="meta-value">
                    <span
                      class="dark placeholder"
                      style="width: 100px"
                    />
                  </div>
                </div>
                <div class="meta-data-group">
                  <div class="meta-label">
                    <span
                      class="dark placeholder"
                      style="width: 80px"
                    />
                  </div>
                  <div class="meta-value">
                    <span
                      class="dark placeholder"
                      style="width: 100px"
                    />
                  </div>
                </div>
              </div>
              <div
                v-else
                class="flex items-start gap-5 md:items-center"
              >
                <div class="meta-data-group">
                  <div class="meta-label">{{ $t("S.LIT_SongNumber") }}</div>
                  <div class="meta-value">
                    <span>
                      {{ store.song?.ccliSongNumber }}
                    </span>
                    <CopyIcon
                      v-if="store.song?.ccliSongNumber"
                      id="songNumberCopyButton"
                      :value="store.song.ccliSongNumber"
                      data-user-activity="Copied Song Number"
                      busy-bg
                    />
                  </div>
                </div>

                <div class="meta-data-group">
                  <div class="meta-label">{{ $t("SV.LIT_DefaultKey") }}</div>
                  <div class="meta-value">
                    {{ store.song?.defaultKey?.join(", ") }}
                  </div>
                  <div
                    v-if="!store.song?.defaultKey?.length"
                    class="meta-value"
                  >
                    {{ $t("S.LIT_Unknown") }}
                  </div>
                </div>

                <div
                  v-if="store.song?.bpm"
                  class="meta-data-group"
                >
                  <div class="meta-label">{{ $t("SV.LIT_BPM") }}</div>
                  <div class="meta-value">
                    {{ store.song?.bpm }}
                  </div>
                </div>
              </div>
            </div>
            <div
              v-if="store.status !== LoadStatus.Ready"
              class="sound-sample-placeholder dark placeholder"
            ></div>
            <NowPlayingFull
              v-else
              progress-bar-only
            />
          </div>
        </div>
      </div>
    </Card>
  </Container>
</template>
<style scoped lang="postcss">
.main-container {
  @apply mb-0 pb-0;
}
.wrapper {
  @apply bg-gray-800 shadow-none;
  :deep() {
    .card-body {
      @apply bg-transparent;
    }
    .bg-img img {
      @apply rotate-180 scale-[200%] blur-2xl brightness-75;
    }
  }
}
.splash-wrapper {
  @apply relative text-white transition-all;
}
h1 {
  @apply text-4xl font-bold md:text-5xl;
}

.authors {
  @apply mr-1 inline;
  &:not(:last-of-type)::after {
    content: ",";
    @apply no-underline;
  }
  &:hover,
  &:focus-visible {
    @apply bg-transparent brightness-90;
  }
}
.meta-data {
  /*@apply flex items-center gap-x-5 gap-y-3 flex-wrap my-3;*/
  @apply my-3 grid gap-3;
}
.meta-data-group {
  @apply items-center gap-2 md:flex;
}
.meta-label {
  @apply text-sm opacity-75;
}
.meta-value {
  @apply text-lg;
  :deep() {
  }
}
.mediaplayer {
  :deep() {
    .album-artwork .content {
      @apply bg-white/10;
    }
  }
}
.album-artwork-wrapper {
  @apply h-24 w-24 md:h-[250px] md:w-[250px];
}
.splash-wrapper:deep() {
  button.copy-btn {
    i,
    svg {
      @apply text-white;
    }
  }
}
.placeholder.sound-sample-placeholder {
  @apply h-[56px] w-full rounded-xl opacity-10;
}
:deep(.media-player-expanded) {
  @apply rounded-xl;
}
.based-on-recording-badge {
  @apply ml-auto block h-12 shrink-0 items-center gap-0 rounded-xl bg-black/25 p-2 px-4 xl:flex;
  .based-on-recording-icon {
    @apply xl:mr-4;
  }
  .based-on-recording-text {
    @apply max-w-[75px] shrink-0 text-xs font-bold uppercase tracking-wider max-xl:hidden;
  }
}
html[lang="de-DE"],
html[lang="de-CH"],
html[lang="es-US"] {
  .based-on-recording-badge .based-on-recording-text {
    @apply max-w-[100px];
  }
}
html[lang="fr-FR"] {
  .based-on-recording-badge .based-on-recording-text {
    @apply max-w-[105px];
  }
}
</style>
